/**
 * EmbeddedCanvasElement - Edit mode component for embedded canvas elements.
 *
 * Displays a preview of the child canvas layout (greyed out) with configuration info.
 * Shows "Not configured" state when no child canvas is selected.
 * Provides "Edit" button to navigate into child canvas inline.
 */

import React from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Sheet,
  Skeleton,
} from "@mui/joy";
import EditIcon from "@mui/icons-material/Edit";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LinkIcon from "@mui/icons-material/Link";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { CanvasElement, Canvas } from "../../../types";

/**
 * Embedding configuration stored in element props.
 */
export interface EmbeddingConfig {
  /** ID of the child canvas to embed */
  childCanvasId?: string | number;
  /** Alias for the parent data source */
  parentDataSourceAlias?: string;
  /** Field path in parent to use as the linking key */
  parentKeyField?: string;
  /** Alias for the child data source */
  childDataSourceAlias?: string;
  /** Field in child canvas to filter by (FK field) */
  childFilterField?: string;
  /** Compact removes the nested card surface, without removing form actions. */
  presentation?: "default" | "compact";
  showTitle?: boolean;
}

export interface EmbeddedCanvasElementProps {
  /** The canvas element definition */
  element: CanvasElement;
  /** Child canvas data (if loaded) */
  childCanvas?: Canvas | null;
  /** Whether the child canvas is loading */
  isLoading?: boolean;
  /** Callback when user clicks Edit to navigate into child canvas */
  onNavigateToChild?: (childCanvasId: string | number) => void;
  /** Whether the element is selected */
  isSelected?: boolean;
  /** Mode: edit for builder, runtime for execution */
  mode?: "edit" | "runtime";
}

/**
 * Gets embedding configuration from element props.
 */
export function getEmbeddingConfig(element: CanvasElement): EmbeddingConfig {
  const props = element.props as unknown as EmbeddingConfig &
    Record<string, unknown>;
  return {
    childCanvasId: props.childCanvasId,
    parentDataSourceAlias: props.parentDataSourceAlias,
    parentKeyField: props.parentKeyField,
    childDataSourceAlias: props.childDataSourceAlias,
    childFilterField: props.childFilterField,
    presentation: props.presentation,
    showTitle: props.showTitle,
  };
}

/**
 * Renders a mini preview of the child canvas layout (greyed out).
 */
function CanvasPreviewMini({ canvas }: { canvas: Canvas }) {
  const elements = canvas.elements || [];

  // Show just a simplified representation of the canvas layout
  if (elements.length === 0) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Empty canvas
        </Typography>
      </Box>
    );
  }

  // Render a simplified grid preview
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        gap: 0.5,
        p: 1,
        opacity: 0.6,
      }}
    >
      {elements.slice(0, 6).map((el, idx) => {
        // Get grid positioning from layout or use defaults
        const columnSpan = el.layout?.columnSpan || 6;
        return (
          <Box
            key={el.id || idx}
            sx={{
              gridColumn: `span ${Math.min(columnSpan, 12)}`,
              height: 16,
              bgcolor: "neutral.200",
              borderRadius: "xs",
            }}
          />
        );
      })}
      {elements.length > 6 && (
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            +{elements.length - 6} more elements
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * EmbeddedCanvasElement - Renders an embedded canvas element in edit mode.
 */
export function EmbeddedCanvasElement({
  element,
  childCanvas,
  isLoading = false,
  onNavigateToChild,
  isSelected = false,
  mode = "edit",
}: EmbeddedCanvasElementProps) {
  const config = getEmbeddingConfig(element);
  const isConfigured = !!config.childCanvasId;
  const hasFilterConfig = !!(config.parentKeyField && config.childFilterField);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (config.childCanvasId && onNavigateToChild) {
      onNavigateToChild(config.childCanvasId);
    }
  };

  // Not configured state
  if (!isConfigured) {
    return (
      <Sheet
        variant="soft"
        color="warning"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "md",
          border: "2px dashed",
          borderColor: "warning.300",
          p: 2,
          gap: 1,
        }}
      >
        <WarningAmberIcon sx={{ fontSize: 32, color: "warning.500" }} />
        <Typography
          level="body-sm"
          sx={{ color: "warning.700", fontWeight: "md" }}
        >
          Embedded Canvas
        </Typography>
        <Typography
          level="body-xs"
          sx={{ color: "text.tertiary", textAlign: "center" }}
        >
          Not configured - select a child canvas in properties
        </Typography>
      </Sheet>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Sheet
        variant="outlined"
        sx={{
          height: "100%",
          borderRadius: "md",
          p: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width={120} />
        </Box>
        <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: "sm" }} />
      </Sheet>
    );
  }

  // Configured state with canvas preview
  return (
    <Sheet
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: "md",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: isSelected ? "primary.500" : "neutral.outlinedBorder",
        borderWidth: isSelected ? 2 : 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          bgcolor: "background.level1",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DashboardIcon sx={{ fontSize: 18, color: "primary.500" }} />
          <Typography level="body-sm" fontWeight="md">
            {childCanvas?.name || element.props.label || "Embedded Canvas"}
          </Typography>
        </Box>
        {mode === "edit" && onNavigateToChild && (
          <Tooltip title="Edit child canvas">
            <IconButton
              size="sm"
              variant="soft"
              color="primary"
              onClick={handleEditClick}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Filter context info */}
      {hasFilterConfig && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.5,
            bgcolor: "primary.softBg",
          }}
        >
          <LinkIcon sx={{ fontSize: 14, color: "primary.600" }} />
          <Typography level="body-xs" sx={{ color: "primary.700" }}>
            {config.parentKeyField} → {config.childFilterField}
          </Typography>
        </Box>
      )}

      {/* Canvas preview */}
      <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {childCanvas ? (
          <CanvasPreviewMini canvas={childCanvas} />
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.level1",
              p: 2,
            }}
          >
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Canvas ID: {config.childCanvasId}
            </Typography>
            {!hasFilterConfig && (
              <Chip size="sm" color="warning" variant="soft" sx={{ mt: 1 }}>
                No filter configured
              </Chip>
            )}
          </Box>
        )}

        {/* Overlay for edit mode to prevent interaction with preview */}
        {mode === "edit" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "transparent",
              cursor: "pointer",
            }}
            onClick={handleEditClick}
          />
        )}
      </Box>

      {/* Footer with canvas type indicator */}
      {childCanvas && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 0.5,
            bgcolor: "background.level2",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Chip size="sm" variant="soft" color="neutral">
            {childCanvas.canvas_type}
          </Chip>
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            {childCanvas.elements?.length || 0} elements
          </Typography>
        </Box>
      )}
    </Sheet>
  );
}

export default EmbeddedCanvasElement;
